import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Input, InputNumber, Select, Button, Space, DatePicker } from 'antd';
import { useConfigQuery } from '../../../queries/useConfig';
import { MoneyInput } from '../../../Componentes/MoneyInput';
import { InvoiceNumberInput } from '../../../Componentes/InvoiceNumberInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faMoneyBillWave, faTrash } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';

const { Option } = Select;

// ====== Helpers para documento CPF/CNPJ ======
const onlyDigits = (val = '') => {
    return val.replace(/\D/g, '').slice(0, 14); // máximo 14 dígitos
};

const formatDocumento = (digits = '') => {
    if (!digits) return '';

    // CPF até 11 dígitos -> 000.000.000-00
    if (digits.length <= 11) {
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return digits.replace(/(\d{3})(\d{1,3})/, '$1.$2');
        if (digits.length <= 9) return digits.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
    }

    // CNPJ 12–14 dígitos -> 00.000.000/0000-00
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return digits.replace(/(\d{2})(\d{1,3})/, '$1.$2');
    if (digits.length <= 8) return digits.replace(/(\d{2})(\d{3})(\d{1,3})/, '$1.$2.$3');
    if (digits.length <= 12) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{1,4})/, '$1.$2.$3/$4');
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
};

// ====== Helpers para pesos (BR visual, 4 casas com ponto no objeto) ======
const formatPesoBRFromStorage = (stored) => {
    if (stored === undefined || stored === null || stored === '') return '';

    const num = Number(stored);
    if (Number.isNaN(num)) return '';

    const fixed = num.toFixed(4);
    const [intPart, decPart] = fixed.split('.');

    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${intWithSep},${decPart}`;
};

// converte texto digitado ("1.234,5678" ou "1234,5") -> "1234.5678"
const parsePesoBRToStorage = (raw) => {
    if (!raw) return '';

    let cleaned = raw.replace(/[^\d,\.]/g, '');

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const sepIndex = Math.max(lastComma, lastDot);

    let intPart;
    let decPart;

    if (sepIndex === -1) {
        intPart = cleaned;
        decPart = '';
    } else {
        intPart = cleaned.slice(0, sepIndex);
        decPart = cleaned.slice(sepIndex + 1);
    }

    intPart = intPart.replace(/\D/g, '');
    decPart = decPart.replace(/\D/g, '');

    if (!intPart && !decPart) return '';

    const joined = decPart ? `${intPart}.${decPart}` : intPart;
    const num = Number(joined);
    if (Number.isNaN(num)) return '';

    return num.toFixed(4);
};

// ====== Datas locais estáveis (sem UTC/toISOString) ======
const toYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const hojeLocalYMD = () => toYMD(new Date());

const parseBaseDate = (v) => {
    if (!v) return null;

    // dayjs/moment
    if (v?.toDate) return v.toDate();

    // Date
    if (v instanceof Date) return v;

    // "YYYY-MM-DD"
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [yy, mm, dd] = v.split("-").map(Number);
        return new Date(yy, mm - 1, dd, 12, 0, 0, 0);
    }

    // "DD/MM/YYYY"
    if (typeof v === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
        const [dd, mm, yy] = v.split("/").map(Number);
        return new Date(yy, mm - 1, dd, 12, 0, 0, 0);
    }

    const dt = new Date(v);
    return isNaN(dt.getTime()) ? null : dt;
};

const CobrancaForm = ({ fatura, setFatura, mostrarCobranca, setMostrarCobranca, precoTotal }) => {
    const { data: config, error } = useConfigQuery();

    const [parcelas, setParcelas] = useState({});

    const [qtdParcelas, setQtdParcelas] = useState(null);
    const [intervalo, setIntervalo] = useState(null); // pode ser 0

    // ✅ agora é "faturar em X dias"
    const [diasPrimeiroVencimento, setDiasPrimeiroVencimento] = useState(0);
    const [vencimentoPrimeiraParcela, setVencimentoPrimeiraParcela] = useState(hojeLocalYMD()); // "YYYY-MM-DD"

    // ✅ sempre que mudar X dias, recalcula a data do primeiro vencimento
    useEffect(() => {
        const dias = Number(diasPrimeiroVencimento ?? 0);
        if (!Number.isFinite(dias)) return;

        const base = new Date();
        base.setHours(12, 0, 0, 0);
        base.setDate(base.getDate() + dias);

        setVencimentoPrimeiraParcela(toYMD(base));
    }, [diasPrimeiroVencimento]);

    const handleRemoveVol = (index) => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        list.splice(index, 1);
        updateParcelas({ dup: list });
    };

    const updateParcelas = (patch) => {
        console.log(patch);
        const newTransp = { ...parcelas, ...patch };
        setParcelas(newTransp);
        setFatura(prev => ({ ...prev, dup: newTransp }));
    };

    const handleParcelasChange = (index, field, newValue) => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        list[index] = { ...(list[index] || getDefaultParcelas()), [field]: newValue };
        updateParcelas({ dup: list });
    };

    const pad3 = (n) => String(n).padStart(3, '0');
    const hoje = hojeLocalYMD();

    const getDefaultParcelas = (numeroParcela = 1) => ({
        nDup: pad3(numeroParcela),
        dVenc: hoje,
        vDup: 0,
    });

    const addParcela = () => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        const proximoNumero = list.length + 1;
        list.push(getDefaultParcelas(proximoNumero));
        updateParcelas({ dup: list });
    };

    const parseBRLToNumber = (v) => {
        if (v === null || v === undefined) return 0;
        if (typeof v === "number") return v;

        const s = String(v).trim();
        if (!s) return 0;

        let cleaned = s.replace(/[^\d,.\-]/g, "");

        if (cleaned.includes(",") && cleaned.includes(".")) {
            cleaned = cleaned.replace(/\./g, "").replace(",", ".");
        } else {
            cleaned = cleaned.replace(",", ".");
        }

        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
    };

    const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

    const addParcelasAutomaticas = () => {
        const n = Number(qtdParcelas || 0);
        const dias = Number(intervalo ?? 0); // permite 0

        if (!n || n <= 0) return;
        if (dias < 0) return;

        // ✅ agora a base vem do cálculo "hoje + X dias"
        const base = parseBaseDate(vencimentoPrimeiraParcela);
        if (!base) return;

        const total = parseBRLToNumber(precoTotal);
        const valorBase = n > 0 ? round2(total / n) : 0;

        const valores = Array.from({ length: n }, () => valorBase);
        const somaParcial = round2(valorBase * n);
        const diff = round2(total - somaParcial);
        valores[n - 1] = round2(valores[n - 1] + diff);

        const list = Array.from({ length: n }, (_, i) => {
            const num = i + 1;

            const venc = new Date(base);
            venc.setDate(venc.getDate() + i * dias);

            const parcela = getDefaultParcelas(num);

            return {
                ...parcela,
                dVenc: toYMD(venc),
                vDup: valores[i],
            };
        });

        updateParcelas({ dup: list });
    };

    const disabledGerarParcelas =
        !qtdParcelas ||
        intervalo === null ||
        intervalo === undefined; // ✅ não bloqueia quando intervalo = 0

    return (
        <div>
            <Card
                title="Fatura"
                extra={
                    <Button
                        onClick={() => setMostrarCobranca((v) => !v)}
                        style={{
                            backgroundColor: "#000",
                            color: "#fff",
                            border: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                        icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    >
                        Cobrança
                    </Button>
                }
            >
                {mostrarCobranca && (
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                    gap: 12,
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Número da fatura</label>
                                    <InvoiceNumberInput
                                        className="input-estilizado"
                                        value={fatura.nFat}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, nFat: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor original</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vOrig}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vOrig: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor do desconto</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vDesc}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vDesc: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor líquido</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vLiq}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vLiq: v }))}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}
            </Card>

            {mostrarCobranca && (
                <div>
                    <Card
                        title="Parcelas"
                        extra={
                            <>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                                gap: 12,
                                            }}
                                        >
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                <label>Quantidade de parcelas</label>
                                                <InputNumber
                                                    className="input-estilizado"
                                                    style={{
                                                        width: "173px",
                                                        height: "44px",
                                                        border: "1px solid #000",
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                    value={qtdParcelas}
                                                    onChange={(v) => setQtdParcelas(v)}
                                                    controls={false}
                                                    inputStyle={{
                                                        textAlign: "center",
                                                        height: "44px",
                                                        lineHeight: "44px",
                                                    }}
                                                />
                                            </div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                <label>Faturar em (dias)</label>
                                                <InputNumber
                                                    className="input-estilizado"
                                                    style={{
                                                        width: "173px",
                                                        height: "44px",
                                                        border: "1px solid #000",
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                    value={diasPrimeiroVencimento}
                                                    onChange={(v) => setDiasPrimeiroVencimento(v ?? 0)}
                                                    controls={false}
                                                    inputStyle={{
                                                        textAlign: "center",
                                                        height: "44px",
                                                        lineHeight: "44px",
                                                    }}
                                                />
                                                {/* opcional: mostra a data calculada */}
                                                <small style={{ opacity: 0.75 }}>
                                                    Venc.: {vencimentoPrimeiraParcela ? dayjs(vencimentoPrimeiraParcela, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-'}
                                                </small>

                                            </div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                <label>Intervalo (dias)</label>
                                                <InputNumber
                                                    className="input-estilizado"
                                                    style={{
                                                        width: "173px",
                                                        height: "44px",
                                                        border: "1px solid #000",
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                    value={intervalo}
                                                    onChange={(v) => setIntervalo(v)}
                                                    controls={false}
                                                    inputStyle={{
                                                        textAlign: "center",
                                                        height: "44px",
                                                        lineHeight: "44px",
                                                    }}
                                                />
                                            </div>



                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 6,
                                                    justifyContent: "flex-end",
                                                }}
                                            >
                                                <label style={{ opacity: 0 }}>Ação</label>
                                                <Button
                                                    style={{
                                                        width: "173px",
                                                        height: "44px",
                                                        backgroundColor: "#000",
                                                        color: "#fff",
                                                        border: "1px solid #000",
                                                    }}
                                                    onClick={addParcelasAutomaticas}
                                                    disabled={disabledGerarParcelas}
                                                >
                                                    Gerar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={addParcela}
                                                    style={{
                                                        width: "173px",
                                                        height: "44px",
                                                        backgroundColor: "#000",
                                                        color: "#fff",
                                                        border: "1px solid #000",
                                                    }}
                                                >
                                                    Adicionar parcela
                                                </Button>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <br />
                            </>
                        }
                    >
                        {parcelas.dup?.map((vol, vIndex) => (
                            <Card
                                key={vIndex}
                                size="small"
                                type="inner"
                                title={`Volume #${vIndex + 1}`}
                                extra={
                                    <Button size="small" danger onClick={() => handleRemoveVol(vIndex)}>
                                        Remover
                                    </Button>
                                }
                                style={{ marginBottom: 12 }}
                            >
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <label>Parcela</label>
                                        <Input
                                            style={{ width: '100%' }}
                                            value={vol.nDup}
                                            onChange={(e) => handleParcelasChange(vIndex, 'nDup', e.target.value)}
                                        />
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <label>Valor</label>
                                        <MoneyInput
                                            style={{ width: '100%' }}
                                            className="input-estilizado"
                                            value={vol.vDup}
                                            onChange={(val) => handleParcelasChange(vIndex, 'vDup', val)}
                                        />
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <label>Vencimento</label>
                                        <Input
                                            type="date"
                                            style={{ width: '100%' }}
                                            value={vol.dVenc}
                                            onChange={(e) => handleParcelasChange(vIndex, 'dVenc', e.target.value)}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </Card>
                </div>
            )}
        </div>
    );
};

export default CobrancaForm;
